<template>
  <div ref="cardRef" class="singleCase-wrapper" :class="wrapperClass" @mouseenter="handleMouseEnter"
    @mousemove="handleMouseMove" @mouseleave="handleMouseLeave" :style="cardStyle">
    <!-- Top Left Badge Slot -->
    <slot name="top-left-badge"></slot>

    <!-- Top Right Badge Slot -->
    <slot name="top-right-badge"></slot>

    <!-- Bottom Left Badge Slot -->
    <slot name="bottom-left-badge"></slot>

    <!-- Background Layer (Depth 1) -->
    <div class="background-layer" :style="backgroundStyle">
      <div class="case-name">{{ caseName }}</div>
    </div>

    <!-- Case Layer (Depth 2) -->
    <div class="case-layer" :style="caseStyle">
      <img class="case-img" :src="caseImage" :alt="caseName" loading="lazy" />
    </div>

    <!-- Weapon Layer (Depth 3) -->
    <div class="weapon-layer" :style="weaponStyle">
      <img class="case-item-img" :src="itemImage" alt="" aria-hidden="true" loading="lazy" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useCardParallax, type ParallaxConfig } from '@/composables/useCardParallax'

interface Props {
  caseName: string
  caseImage: string
  itemImage: string
  wrapperClass?: string | object | Array<string | object>
  parallaxConfig?: Partial<ParallaxConfig>
}

const props = defineProps<Props>()

const cardRef = ref<HTMLElement>()

const { cardStyle, backgroundStyle, caseStyle, weaponStyle, handleMouseMove, handleMouseEnter, handleMouseLeave } =
  useCardParallax(cardRef, props.parallaxConfig)
</script>

<style lang="scss" scoped>
@use 'sass:color';

.singleCase-wrapper {
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 235px;
  height: 300px;
  border-radius: 12px;
  overflow: hidden;
  perspective: 1000px;
  transform-style: preserve-3d;
  will-change: transform;
  contain: layout style paint;

  @media (max-width: 1536px) {
    width: 215px;
    height: 285px;
  }

  @include mobile {
    width: 175px;
    height: 260px;
  }

  &:hover {
    .background-layer {
      box-shadow: 0 16px 32px rgba(0, 0, 0, 0.25);
      border-color: rgba(255, 255, 255, 0.4);
    }

    .weapon-layer {
      filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.3));
    }

    .case-name {
      background-color: rgba(0, 0, 0, 0.85);
      border-color: rgba(255, 255, 255, 0.4);
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
      color: rgba(255, 255, 255, 1);
    }
  }

  &:active {
    .background-layer {
      border-color: rgba(255, 255, 255, 0.25);
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
    }

    .case-name {
      background-color: rgba(255, 255, 255, 0.08);
      border-color: rgba(255, 255, 255, 0.25);
      color: rgba(255, 255, 255, 1);
      transform: translateX(-50%) scale(0.95);
    }
  }

  .background-layer {
    position: absolute;
    inset: 0;
    background:
      repeating-linear-gradient(45deg,
        rgba(25, 35, 50, 0.8) 0px,
        rgba(25, 35, 50, 0.8) 80px,
        rgba(15, 23, 42, 0.9) 80px,
        rgba(15, 23, 42, 0.9) 160px),
      linear-gradient(135deg, rgba(6, 9, 24, 0.95) 0%, rgba(3, 7, 18, 1) 100%);
    border-radius: 12px;
    border: none;
    transition: all 0.3s ease;
    transform: translateZ(-20px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(180deg,
          rgba(255, 255, 255, 0.1) 0%,
          rgba(255, 255, 255, 0.02) 50%,
          transparent 100%);
      border-radius: 12px;
    }

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(45deg,
          transparent 30%,
          rgba(255, 255, 255, 0.2) 50%,
          transparent 70%);
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    &:hover::after {
      opacity: 1;
    }
  }

  .weapon-layer {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.1s ease-out;
    transform: translateZ(20px);
    z-index: 3;
  }

  .case-name {
    position: absolute;
    bottom: 12px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    font-weight: 700;
    color: #fff;
    text-decoration: none;
    background-color: rgba(0, 0, 0, 0.85);
    padding: 0.6em 1em;
    border-radius: 8px;
    width: calc(100% - 24px);
    max-width: 200px;
    text-align: center;
    border: 1px solid rgba(255, 255, 255, 0.1);
    z-index: 5;
    transition: all 0.3s ease;
  }

  .case-layer {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.1s ease-out;
    transform: translateZ(0px);
    z-index: 2;
  }

  .case-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 8px;
    transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    will-change: transform;
  }

  .case-item-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    filter: drop-shadow(rgba(45, 42, 35, 0.71) -0.43em 0.18em 0.25em);
    pointer-events: none;
    will-change: transform;
  }

  &.singleCase-wrapper--image-container {
    width: 214px;
    height: 297px;
    border-radius: 6px;
    background: transparent;
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      z-index: 3;
      border-radius: 6px;
      border: 1px solid transparent;
      background: linear-gradient(
        110deg,
        transparent 0%,
        transparent 38%,
        rgba(255, 255, 255, 0.32) 48%,
        transparent 58%,
        transparent 100%
      );
      background-size: 260% 100%;
      background-position: 140% 0;
      opacity: 0;
      pointer-events: none;
      transition:
        opacity 0.18s ease,
        background-position 0.55s ease,
        border-color 0.2s ease,
        box-shadow 0.2s ease;
    }

    @include mobile {
      width: 165px;
      height: 229px;
    }

    &:hover {
      &::after {
        background-position: -80% 0;
        border-color: rgba(200, 242, 79, 0.55);
        box-shadow:
          inset 0 0 0 1px rgba(255, 255, 255, 0.12),
          0 0 24px rgba(200, 242, 79, 0.18);
        opacity: 1;
      }

      .background-layer {
        box-shadow: none;
        border-color: transparent;
      }

      .case-img {
        filter: brightness(1.3) saturate(1.22) contrast(1.04);
        transform: scale(1.14);
      }

      .case-name {
        background-color: rgba(10, 12, 18, 0.92);
        border-color: rgba(200, 242, 79, 0.48);
        box-shadow: 0 0 16px rgba(200, 242, 79, 0.16);
      }
    }

    &:active {
      .background-layer {
        box-shadow: none;
        border-color: transparent;
      }

      .case-name {
        background-color: rgba(10, 12, 18, 0.92);
        border-color: rgba(255, 255, 255, 0.16);
        transform: translateX(-50%) scale(0.98);
      }
    }

    .background-layer {
      background: transparent;
      border-radius: 6px;
      box-shadow: none;
      overflow: visible;
      z-index: 4;

      &::before,
      &::after {
        display: none;
      }
    }

    .case-layer {
      z-index: 1;
    }

    .case-img {
      object-fit: cover;
      border-radius: 6px;
      filter: brightness(1.18) saturate(1.08);
      transform: scale(1.1);
      transition:
        filter 0.22s ease,
        transform 0.22s ease;
    }

    .weapon-layer {
      display: none;
    }

    .case-name {
      bottom: 12px;
      width: max-content;
      min-width: 118px;
      max-width: none;
      min-height: 38px;
      padding: 0.55rem 0.85rem;
      border-radius: 6px;
      background-color: rgba(10, 12, 18, 0.92);
      border: 1px solid rgba(255, 255, 255, 0.12);
      font-size: 0.9rem;
      line-height: 1;
      text-transform: uppercase;
      white-space: nowrap;
      z-index: 5;

      @include mobile {
        min-width: 92px;
        max-width: none;
        min-height: 34px;
        padding: 0.5rem 0.65rem;
        font-size: 0.78rem;
      }
    }
  }
}
</style>
